import { useEffect, useRef } from 'react';
import type { Layout, QuestionChoice } from '@mentimeter/http-clients';
import {
  useQuizMetaContext,
  useQuizResults,
  useFollowerQuizGameState,
} from '@mentimeter/quiz';
import type { QuizResultData } from '@mentimeter/core-hooks';
import { GameStateEnums } from '@mentimeter/quiz-types';
import { CenteringContainer } from '../../shared/components/quiz/CenteringContainer';
import { Lobby } from '../../shared/components/quiz/lobby';
import { GetReady } from '../../shared/components/quiz/get-ready';
import { Countdown } from '../../shared/components/quiz/countdown';
import { Error } from '../../shared/components/quiz/error';
import { Loading } from '../../shared/components/quiz/loading';
import { TitleSection } from '../../shared/components/title-section/TitleSection';
import { QuizBarsDataHandler } from './QuizBarsDataHandler';

export const FollowerPresentation = ({
  activeSession,
  question,
  questionStyled,
  meta,
  description,
  choices,
  imageUrl,
  timeBasedScoring,
  shouldShowLegacyImage,
  layout,
}: {
  activeSession: number;
  imageUrl: string | undefined;
  timeBasedScoring: boolean;
  question: string;
  questionStyled: Record<string, unknown>;
  meta: string;
  description: string;
  choices: QuestionChoice[];
  shouldShowLegacyImage: boolean;
  layout: Layout | undefined;
}) => {
  const { totalCount, currentIndex } = useQuizMetaContext();

  const { data = [], isPreviewResults } = useQuizResults<QuizResultData>();
  const {
    gameState,
    connectedPlayers,
    respondents,
    joinAsFollower,
    connectedWithDelay,
    timeOffset = 0,
  } = useFollowerQuizGameState();

  const prevActiveSession = useRef(activeSession);
  const createdRef = useRef(false);
  useEffect(() => {
    const sessionChanged = prevActiveSession.current !== activeSession;
    if ((!gameState && !createdRef.current) || sessionChanged) {
      joinAsFollower();
    }
  }, [joinAsFollower, activeSession, gameState]);

  if (isPreviewResults) {
    return (
      <>
        <TitleSection
          title={question}
          styledTitle={questionStyled}
          label={meta}
          description={description}
          fontSizeOffset={layout?.font_size_offset}
        />
        <CenteringContainer>
          <QuizBarsDataHandler
            showCorrect={data.length > 0}
            rawResults={data}
            choices={choices}
            enableAnimations
          />
        </CenteringContainer>
      </>
    );
  }

  switch (gameState?.gameState) {
    case GameStateEnums.LOBBY: {
      return (
        <CenteringContainer data-testid="gamestate-lobby">
          <Lobby
            connected={connectedWithDelay}
            total={totalCount}
            current={currentIndex + 1}
            players={connectedPlayers}
          />
        </CenteringContainer>
      );
    }
    case GameStateEnums.GET_READY: {
      return (
        <CenteringContainer>
          <GetReady timeBasedScoring={timeBasedScoring} />
        </CenteringContainer>
      );
    }
    case GameStateEnums.COUNTDOWN: {
      return (
        <>
          <TitleSection
            title={question}
            styledTitle={questionStyled}
            label={meta}
            description={description}
            fontSizeOffset={layout?.font_size_offset}
          />
          <CenteringContainer>
            <Countdown
              type="choices"
              respondents={respondents.length}
              everyoneHasVoted={false}
              imageUrl={imageUrl}
              timeOffset={timeOffset}
              startAt={gameState.gameStateData.startAt}
              endAt={gameState.gameStateData.endAt}
              shouldShowLegacyImage={shouldShowLegacyImage}
            >
              <QuizBarsDataHandler
                showCorrect={false}
                choices={choices}
                enableAnimations={false}
                rawResults={[]}
              />
            </Countdown>
          </CenteringContainer>
        </>
      );
    }
    case GameStateEnums.RESULT: {
      return (
        <>
          <TitleSection
            title={question}
            styledTitle={questionStyled}
            label={meta}
            description={description}
            fontSizeOffset={layout?.font_size_offset}
          />
          <CenteringContainer mt={4}>
            <QuizBarsDataHandler
              showCorrect
              rawResults={data}
              choices={choices}
              enableAnimations
            />
          </CenteringContainer>
        </>
      );
    }
    case GameStateEnums.ERROR: {
      return (
        <CenteringContainer>
          <Error errorCode={gameState.errorCode} />
        </CenteringContainer>
      );
    }
    default: {
      return (
        <CenteringContainer>
          <Loading
            onReload={() => {
              window.location.reload();
            }}
          />
        </CenteringContainer>
      );
    }
  }
};
